import React, { useState } from "react";
import { Box, Grid, Typography, TextField, Button, Snackbar, Alert } from "@mui/material";
import { ref, push } from 'firebase/database';
import { database } from '../../firebase';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const RequestCallback: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const sanitizeInput = (input: string): string => {
    return input.replace(/[<>]/g, '').trim();
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters long';
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Subject validation
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
      isValid = false;
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.length < 10) {
      errors.message = 'Message must be at least 10 characters long';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before submitting.',
        severity: 'error'
      });
      return;
    }

    try {
      const callbackRequestsRef = ref(database, 'callbackRequests');
      const newCallbackRequest = {
        ...formData,
        createdOn: new Date().toISOString()
      };
      await push(callbackRequestsRef, newCallbackRequest);

      setSnackbar({
        open: true,
        message: 'Your message has been sent successfully!',
        severity: 'success'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setValidationErrors({});
    } catch (error) {
      console.error('Error sending callback request:', error);
      setSnackbar({
        open: true,
        message: 'Error sending message. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        backgroundColor: "#8BC34A", // Green background
        padding: "60px 20px",
        textAlign: "center",
        borderRadius: "8px",
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          fontFamily: "'Poppins', sans-serif",
          marginBottom: "10px",
          color: "#000000", // Black for main text
        }}
      >
        Request a{" "}
        <Typography
          variant="h4"
          component="span"
          sx={{
            fontWeight: "bold",
            fontFamily: "'Poppins', sans-serif",
            color: "#000000", // Black for main text
          }}
        >
          call back
        </Typography>
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#000000", // Black subtitle text
          fontFamily: "'Poppins', sans-serif",
          marginBottom: "40px",
        }}
      >
       
      </Typography>

      {/* Form Section */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        <Grid container spacing={2}>
          {/* Full Name */}
          <Grid item xs={12} sm={4}>
            <TextField
              required
              name="name"
              value={formData.name}
              onChange={handleChange}
              variant="outlined"
              placeholder="Full Name"
              fullWidth
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  padding: "10px 20px",
                },
              }}
            />
          </Grid>

          {/* Email Address */}
          <Grid item xs={12} sm={4}>
            <TextField
              required
              name="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              placeholder="E-Mail Address"
              fullWidth
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  padding: "10px 20px",
                },
              }}
            />
          </Grid>

          {/* Subject */}
          <Grid item xs={12} sm={4}>
            <TextField
              required
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              variant="outlined"
              placeholder="Subject"
              fullWidth
              error={!!validationErrors.subject}
              helperText={validationErrors.subject}
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  padding: "10px 20px",
                },
              }}
            />
          </Grid>

          {/* Message */}
          <Grid item xs={12}>
            <TextField
              required
              name="message"
              value={formData.message}
              onChange={handleChange}
              variant="outlined"
              placeholder="Your Message"
              fullWidth
              multiline
              rows={4}
              error={!!validationErrors.message}
              helperText={validationErrors.message}
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "15px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                  padding: "10px 20px",
                },
              }}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#000000", // Black button background
                color: "#FFFFFF", // White text
                fontWeight: "bold",
                fontFamily: "'Poppins', sans-serif",
                padding: "12px 30px",
                borderRadius: "50px",
                "&:hover": {
                  backgroundColor: "#333333", // Slightly lighter black on hover
                },
                maxWidth: "200px",
                margin: "0 auto",
              }}
            >
              Send Message
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestCallback;
